// ─────────────────────────────────────────────────────────────────────────────
// deltaSelection.ts — ONE SELECTION, THREE TABS. THE STORE, NOT THE COMPONENT.
//
// OWNER RULING: "A tab showing a different company from the one selected is the
// wrong artefact for the wrong business." That is a CORRECTNESS requirement, not
// a convenience, and it decides the whole shape of this file.
//
// ── ONE STORE, THREE READERS ───────────────────────────────────────────────
// `Company Research & Diagnosis`, `Calls, Objections & Pivots` and
// `Consultation & Pitch` READ this. None of them fetches its own selection.
// Duplicated fetch logic is the exact failure mode the one-core rule exists to
// prevent, and HERE it would put two different companies on screen at once —
// the owner reading a script for one business while the research panel shows
// another. There is no version of that which is a cosmetic bug.
//
// ── THE SELECTION IS THREE-VALUED, NOT TWO ─────────────────────────────────
//   'unread'   we have not read it — NOT "nothing is selected"
//   'none'     we read it and nothing is selected
//   'selected' we read it and this is the company
// A tab that cannot read the selection renders UNREAD. It never renders the
// last company it happened to hold, and it never renders 'none' from silence —
// never invent a state, in either direction. The third value is the point:
// inferring the good state from the absence of a bad one is the fault this
// avoids, and 'none' is as much a claim as 'selected'.
//
// ── AN ARTEFACT CARRIES THE COMPANY IT WAS PRODUCED FOR ────────────────────
// Every script, every research panel, every deck is stamped with the
// `company_id` it was BUILT for, and `artefactFor` hands it back only when that
// equals the current selection. Not a refetch, not an invalidation timer — an
// identity check at the render. A script for the previous company cannot
// survive a selection change for a single frame, because the frame that would
// show it is the frame that fails the check.
// ─────────────────────────────────────────────────────────────────────────────

export type SelectionPhase = 'unread' | 'none' | 'selected';

export interface Selection {
  phase: SelectionPhase;
  /** The Places id. `null` unless phase is 'selected' — a company id sitting
   *  beside phase 'unread' is exactly the stale-and-confident render this
   *  module exists to make impossible. */
  companyId: string | null;
}

/** Anything a tab renders for a company. The stamp is not optional: an artefact
 *  that cannot say who it is for cannot be shown to anyone. */
export interface CompanyArtefact {
  companyId: string;
}

export const UNREAD: Selection = { phase: 'unread', companyId: null };
export const NONE: Selection = { phase: 'none', companyId: null };

export function select(companyId: string): Selection {
  const id = String(companyId || '').trim();
  // AN EMPTY ID IS NOT A SELECTION. Returning `{selected, ''}` would put every
  // artefact whose id is also empty on screen — a wildcard, silently.
  return id ? { phase: 'selected', companyId: id } : NONE;
}

/**
 * The artefact, or `null` — the identity check, at the render.
 *
 * `null` covers all four ways this can fail and the caller must render its own
 * honest state for each: unread selection, no selection, no artefact, or an
 * artefact belonging to another company.
 */
export function artefactFor<T extends CompanyArtefact>(
  selection: Selection,
  artefact: T | null | undefined,
): T | null {
  if (!selection || selection.phase !== 'selected' || !selection.companyId) return null;
  if (!artefact || !artefact.companyId) return null;
  return artefact.companyId === selection.companyId ? artefact : null;
}

/** What a tab says when `artefactFor` returned null. A FEW WORDS — a control
 *  answers the question, it does not explain its own epistemology. */
export function selectionCaption(selection: Selection): string {
  if (!selection || selection.phase === 'unread') return 'UNREAD';
  if (selection.phase === 'none') return 'NO COMPANY SELECTED';
  return '';
}

// ── THE STORE ──────────────────────────────────────────────────────────────
// Module-level, because there is exactly one selection in the app and three
// surfaces read it. A React context would be the same single value with a
// provider boundary; this is reachable from a non-React caller too, and the
// subscription is what the tabs use.

type Listener = (s: Selection) => void;

let current: Selection = UNREAD;
const listeners = new Set<Listener>();

export function getSelection(): Selection {
  return current;
}

export function setSelection(next: Selection): void {
  current = next;
  listeners.forEach((fn) => fn(current));
}

export function subscribeSelection(fn: Listener): () => void {
  listeners.add(fn);
  fn(current);
  return () => {
    listeners.delete(fn);
  };
}

/** Test seam ONLY. Never a production handle — a test seam carries test
 *  semantics with it, and this one resets a store three tabs read. */
export function __resetSelectionForTest(): void {
  current = UNREAD;
  listeners.clear();
}
